import { json, postBiz, rows } from '../core/coreapi';
import {
  HistoricalFactFilters,
  HistoricalFactSaveRecord,
  HistoricalFilters,
  HistoricalIssueFilters,
  HistoricalGapPage,
  HistoricalGapSummary,
  MatrixCommitResponse,
  MatrixResolutionSaveRecord,
  PagedResult,
} from './bieHistorical.models';
import {
  normalizeHistoricalFactRow,
  normalizeHistoricalImportRow,
  normalizeHistoricalIntegrityIssue,
  normalizeHistoricalGapRow,
  normalizeHistoricalWorkbench,
} from './bieHistorical.normalize';

const SERVICE = 'biehistorical';

const numberOf = (value: unknown, fallback = 0): number => {
  const result = Number(value);
  return Number.isFinite(result) ? result : fallback;
};

const paged = <T>(
  response: any,
  mapper: (row: any) => T,
  page: number,
  pageSize: number,
): PagedResult<T> => {
  const payload = json(response) ?? {};
  const sourceRows =
    (Array.isArray(payload) ? payload : undefined) ??
    payload.rows ?? payload.data ?? payload.items ?? payload.rcd ?? payload.sd ?? rows(response) ?? [];
  const normalizedRows = Array.isArray(sourceRows) ? sourceRows.map(mapper) : [];

  return {
    rows: normalizedRows,
    rowCount: numberOf(
      payload.rowCount ?? payload.rowcount ?? payload.total ?? payload.count ?? payload.cnt ??
      (typeof payload.rc === 'number' ? payload.rc : undefined),
      normalizedRows.length,
    ),
    page: numberOf(payload.page ?? payload.pos, page),
    pageSize: numberOf(payload.pageSize ?? payload.pagesize ?? payload.plm, pageSize),
  };
};

export const fetchHistoricalWorkbench = async (planningcycleid: number) =>
  normalizeHistoricalWorkbench(
    json(await postBiz({
      s: SERVICE,
      a: 'workbench',
      pci: planningcycleid,
    })),
  );

export const fetchHistoricalImportsPage = async (
  filters: HistoricalFilters,
): Promise<PagedResult<ReturnType<typeof normalizeHistoricalImportRow>>> => {
  const page = filters.page ?? 0;
  const pageSize = filters.pageSize ?? 25;
  const response = await postBiz({
    s: SERVICE,
    a: 'imports',
    pci: filters.planningcycleid,
    hdi: filters.historicaldatasetid ?? null,
    scc: filters.scenarioCode ?? null,
    soc: filters.sourceCode ?? null,
    fyr: filters.fiscalYear ?? null,
    src: filters.search ?? null,
    srt: filters.sortField ?? null,
    sdr: filters.sortDirection ?? null,
    sts: 1,
    pos: page,
    plm: pageSize,
  });
  return paged(response, normalizeHistoricalImportRow, page, pageSize);
};

export const fetchHistoricalImports = async (filters: HistoricalFilters) =>
  (await fetchHistoricalImportsPage(filters)).rows;

export const fetchHistoricalFactsPage = async (
  filters: HistoricalFactFilters,
): Promise<PagedResult<ReturnType<typeof normalizeHistoricalFactRow>>> => {
  const page = filters.page ?? 0;
  const pageSize = filters.pageSize ?? 50;
  const response = await postBiz({
    s: SERVICE,
    a: 'facts',
    pci: filters.planningcycleid,
    hdi: filters.historicaldatasetid ?? null,
    imi: filters.importid ?? null,
    tri: filters.trackid ?? null,
    fyr: filters.fiscalYear ?? null,
    mtc: filters.metricCode ?? null,
    pdc: filters.productCode ?? null,
    pec: filters.periodCode ?? null,
    src: filters.search ?? null,
    srt: filters.sortField ?? null,
    sdr: filters.sortDirection ?? null,
    pos: page,
    plm: pageSize,
  });
  return paged(response, normalizeHistoricalFactRow, page, pageSize);
};



export const fetchHistoricalIssuesPage = async (
  filters: HistoricalIssueFilters,
): Promise<PagedResult<ReturnType<typeof normalizeHistoricalIntegrityIssue>>> => {
  const page = filters.page ?? 0;
  const pageSize = filters.pageSize ?? 25;
  const response = await postBiz({
    s: SERVICE,
    a: 'issues',
    pci: filters.planningcycleid,
    hdi: filters.historicaldatasetid ?? null,
    svc: filters.severityCode ?? null,
    ctc: filters.categoryCode ?? null,
    rsv: filters.resolved ?? null,
    src: filters.search ?? null,
    srt: filters.sortField ?? null,
    sdr: filters.sortDirection ?? null,
    pos: page,
    plm: pageSize,
  });
  return paged(response, normalizeHistoricalIntegrityIssue, page, pageSize);
};

export const fetchHistoricalFacts = async (filters: HistoricalFactFilters) =>
  (await fetchHistoricalFactsPage(filters)).rows;

export const saveHistoricalFacts = async (
  planningcycleid: number,
  historicaldatasetid: number | null | undefined,
  records: HistoricalFactSaveRecord[],
) =>
  json(await postBiz({
    s: SERVICE,
    a: 'savefacts',
    pci: planningcycleid,
    hdi: historicaldatasetid ?? null,
    rcd: records,
  }));

export const generateHistoricalMatrix = async (params: {
  planningcycleid: number;
  historicaldatasetid?: number | null;
  metricCode: string;
  fiscalYear: string;
  productCodes?: string[];
}) =>
  rows(await postBiz({
    s: SERVICE,
    a: 'matrix',
    pci: params.planningcycleid,
    hdi: params.historicaldatasetid ?? null,
    mtc: params.metricCode,
    fyr: params.fiscalYear,
    pdc: params.productCodes ?? null,
  })).map(normalizeHistoricalFactRow);

export const downloadHistoricalTemplate = async (params: {
  planningcycleid: number;
  historicaldatasetid?: number | null;
  metricCode?: string;
  fiscalYear?: string;
}) =>
  json(await postBiz({
    s: SERVICE,
    a: 'template',
    pci: params.planningcycleid,
    hdi: params.historicaldatasetid ?? null,
    mtc: params.metricCode ?? null,
    fyr: params.fiscalYear ?? null,
  }));

export const validateHistoricalWorkbook = async (filePayload: Record<string, unknown>) =>
  json(await postBiz({ s: SERVICE, a: 'validateworkbook', ...filePayload }));

export const importHistoricalWorkbook = async (filePayload: Record<string, unknown>) =>
  json(await postBiz({ s: SERVICE, a: 'importworkbook', ...filePayload }));

export const validateHistoricalCoverage = async (planningcycleid: number) =>
  json(await postBiz({
    s: SERVICE,
    a: 'validate',
    pci: planningcycleid,
  }));

export const setHistoricalGapStrategy = async (planningcycleid: number, strategycode: string ) => 
  json(await postBiz({
    s: SERVICE,
    a: 'setgapstrategy',
    pci: planningcycleid,
    sgc: strategycode,
  }));

export const completeHistoricalPhase = async (
  planningcycleid: number,
  notes = '',
) =>
  json(await postBiz({
    s: SERVICE,
    a: 'complete',
    pci: planningcycleid,
    nts: notes,
  }));

export const commitMatrixResolutions = async (
  planningcycleid: number,
  historicaldatasetid: number | null | undefined,
  records: MatrixResolutionSaveRecord[],
): Promise<MatrixCommitResponse> =>
  json(await postBiz({
    s: SERVICE,
    a: 'matrixcommit',
    pci: planningcycleid,
    hdi: historicaldatasetid ?? null,
    rcd: records,
  }));

export const fetchHistoricalGapsPage = async (params: {
  planningcycleid: number;
  historicaldatasetid?: number | null;
  search?: string | null;
  page?: number;
  pageSize?: number;
}): Promise<HistoricalGapPage> => {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 50;

  const response = await postBiz({
    s: SERVICE,
    a: 'gaps',
    pci: params.planningcycleid,
    hdi: params.historicaldatasetid ?? null,
    src: params.search ?? null,
    pos: page * pageSize,
    plm: pageSize,
  });

  const payload: any = json(response) ?? {};
  const sourceRows = Array.isArray(payload.rows) ? payload.rows : [];
  const summaryRaw: any = payload.summary ?? {};
  const summary: HistoricalGapSummary = {
    gapcount: numberOf(summaryRaw.gapcount),
    filteredcount: numberOf(summaryRaw.filteredcount, sourceRows.length),
    yearcount: numberOf(summaryRaw.yearcount),
    metriccount: numberOf(summaryRaw.metriccount),
    estimatecount: numberOf(summaryRaw.estimatecount),
    manualcount: numberOf(summaryRaw.manualcount),
    historicalyears: Array.isArray(summaryRaw.historicalyears)
      ? summaryRaw.historicalyears.map(String)
      : [],
  };

  return {
    rows: sourceRows.map(normalizeHistoricalGapRow),
    rowCount: summary.filteredcount,
    page,
    pageSize,
    summary,
  };
};
